import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import {
  useDropdownListMaterialGroup1Query,
  useFilterRequireAttentionMaterialLazyQuery,
  useFilterSoldToDomesticOrderLazyQuery,
  useListSourceOfAppQuery,
  useScgpMeQuery,
  useSearchSuggestionDomesticShipTosLazyQuery
} from "@saleor/graphql";
import { FIELD_TYPE } from "@saleor/PendingOrder/components/OrderFilterSectionAdmin/constant";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getProductGroup } from "./utils";

const usePendingOrderAdmin = ({ handleChangeFilter }) => {
  const intl = useIntl();
  const transactionList = [
    {
      label: intl.formatMessage({
        defaultMessage: "Normal"
      }),
      value: "Normal"
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Delivery block"
      }),
      value: "Delivery block"
    }
  ];
  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });
  const { data: sourceOfApp } = useListSourceOfAppQuery();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: [],
      materialNoMaterialDescription: [],
      createDate: {
        gte: null,
        lte: null
      },
      requestDeliveryDate: {
        gte: null,
        lte: null
      },
      bu: "",
      saleOrg: "",
      productGroup: "All",
      shipTo: "",
      poNo: "",
      soNo: "",
      transactions: transactionList.map(item => item.value),
      sourceOfApp: "All"
    },
    onSubmit: data => {
      const submitData = {
        ...data,
        productGroups: data.productGroup,
        saleOrg: formik.values.saleOrg ? [formik.values.saleOrg] : ["All"]
      };
      delete submitData.productGroup;

      if (!submitData.createDate.gte || !submitData.createDate.lte) {
        delete submitData.createDate;
      }
      if (
        !submitData.requestDeliveryDate.gte ||
        !submitData.requestDeliveryDate.lte
      ) {
        delete submitData.requestDeliveryDate;
      }

      for (const [key, value] of Object.entries(submitData)) {
        if (!value) {
          delete submitData[key];
        }
      }

      handleChangeFilter(submitData);
    }
  });

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const [
    getRequireAttentionSoldTo,
    {
      data: fetchRequireAttentionSoldTo,
      loading: loadingRequireAttentionSoldTo
    }
  ] = useFilterSoldToDomesticOrderLazyQuery();

  const [
    getSuggestionDomesticShipTos,
    {
      data: fetchSuggestionDomesticShipTos,
      loading: loadingSuggestionDomesticShipTos
    }
  ] = useSearchSuggestionDomesticShipTosLazyQuery();

  const [
    getRequireAttentionMaterial,
    {
      data: fetchRequireAttentionMaterial,
      loading: loadingRequireAttentionMaterial
    }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    setTimeout(() => setIsClean(false), 500);
  }, []);
  const productGroupList = useMemo(() => {
    if (formik.values.bu === "CIP") {
      formik.setFieldValue("productGroup", "");
      return [];
    }
    formik.setFieldValue("productGroup", "All");
    return getProductGroup(
      productGroup,
      intl.formatMessage({ defaultMessage: "All" })
    );
  }, [formik.values.bu, productGroup]);
  const sourceOfAppListPen = useMemo(
    () =>
      sourceOfApp?.listSourceOfApp.map(({ value }) => ({
        label: value,
        value
      })),
    [sourceOfApp?.listSourceOfApp]
  );
  const { data: scgpMe } = useScgpMeQuery({
    displayLoader: true
  });
  const buList = useMemo(
    () =>
      scgpMe?.scgpMe?.extendData?.scgpBus?.map(bu => ({
        label: bu.name,
        value: bu.name
      })),
    [scgpMe?.scgpMe?.extendData?.scgpBus]
  );
  const saleOrgList = useMemo(
    () =>
      scgpMe?.scgpMe?.extendData?.salesOrgByBu
        ?.find(org => org?.businessUnit === formik?.values?.bu)
        ?.salesOrganizations?.map(e => ({
          label: `${e.code} - ${e.shortName ? e.shortName : ""}`,
          value: e.code
        })) || [],
    [scgpMe?.scgpMe?.extendData?.salesOrgByBu, formik?.values?.bu]
  );

  useEffect(() => {
    const extendData = scgpMe?.scgpMe?.extendData;

    const bu = formik?.values?.bu || extendData?.defaultBusinessUnit?.name;

    const salesOrgByBuAndUser =
      extendData?.salesOrgByBuAndUser?.find(org => org?.businessUnit === bu)
        ?.salesOrganizations || [];

    formik.setValues(prev => ({
      ...prev,
      bu,
      saleOrg:
        salesOrgByBuAndUser?.length === 1 ? salesOrgByBuAndUser[0].code : ""
    }));
  }, [
    scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name,
    formik?.values?.bu
  ]);

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold to"
        }),
        name: "soldTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sold-to"
        }),
        headerText: "Sold-to ที่เลือกค้นหา",
        keyFilterApi: "search",
        message: "ยังไม่มีข้อมูลที่เลือกค้นหา",
        type: FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT,
        maxLength: 35,
        md: 6,
        isShowSelected: true,
        subTitle: "(เลือกค้นหา Sold-to ได้ไม่เกิน 10 รายการ)"
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        subTitle: "(เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "materialNoMaterialDescription",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code / Description"
        }),
        type: FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT,
        message: "ยังไม่มีข้อมูลที่เลือกค้นหา",
        maxLength: 35,
        headerText: "Material Code / Description ที่เลือกค้นหา",
        keyFilterApi: "material",
        md: 6,
        isShowSelected: false
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Create Date" }),
        name: "createDate",
        required:
          !formik.values.soNo &&
          !formik.values.poNo &&
          !(
            formik.values.requestDeliveryDate.gte &&
            formik.values.requestDeliveryDate.lte
          ),
        type: FIELD_TYPE.DATEPICKER,
        maxLength: 35,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "Request Date" }),
        name: "requestDeliveryDate",
        type: FIELD_TYPE.DATEPICKER,
        maxLength: 35,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก BU"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: buList || []
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Organization" }),
        name: "saleOrg",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sales Org."
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        required: !formik.values.soNo && !formik.values.poNo,
        options: saleOrgList || [],
        ignoreRerender: true
      },
      {
        title: intl.formatMessage({ defaultMessage: "Product Group" }),
        name: "productGroup",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Product Group"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: productGroupList,
        disabled: formik.values.bu === "CIP"
      },
      {
        title: intl.formatMessage({ defaultMessage: "Ship-to" }),
        name: "shipTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Ship-to"
        }),
        keyFilterApi: "search",
        type: FIELD_TYPE.SUGGESTION_INPUT,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        name: "poNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "S/O No."
        }),
        name: "soNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ S/O No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "Transaction" }),
        name: "transactions",
        type: FIELD_TYPE.MULTIPLE_SELECT,
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Transaction"
        }),
        options: transactionList,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "Source of App" }),
        name: "sourceOfApp",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Source of App"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: sourceOfAppListPen || [],
        md: 4
      }
    ],
    [
      buList,
      saleOrgList,
      productGroupList,
      sourceOfAppListPen,
      formik.values.soNo,
      formik.values.poNo,
      formik.values.requestDeliveryDate
    ]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getRequireAttentionSoldTo,
      fetchRequireAttentionSoldTo,
      loadingRequireAttentionSoldTo,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      getSuggestionDomesticShipTos,
      fetchSuggestionDomesticShipTos,
      loadingSuggestionDomesticShipTos
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getRequireAttentionSoldTo,
      fetchRequireAttentionSoldTo,
      loadingRequireAttentionSoldTo,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      getSuggestionDomesticShipTos,
      fetchSuggestionDomesticShipTos,
      loadingSuggestionDomesticShipTos
    ]
  );
};

export default usePendingOrderAdmin;
